import { CheckOutlined, PushpinFilled } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Tooltip,
} from 'antd';
import { getConceptosDeAcreditacion } from 'api/catalogos/conceptosDeAcreditacion';
import { getRecibosPDF, postReciboAcreditador } from 'api/recaudacion/recibos';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { recalculateCargos, sortCargos } from 'utils/cargos';
import { onError } from 'utils/handlers';
import Previewer from 'components/Previewer';
import { SET_PADRON_REQUIERE_FETCH } from 'store/reducers/caja';

const { useForm } = Form;

// Constantes para estilos y configuración
const BUTTON_STYLES = {
  borderRadius: 4,
};

const SUCCESS_BUTTON_STYLES = {
  ...BUTTON_STYLES,
  backgroundColor: '#008F39',
  color: '#FFFFFF',
};

const TEXTAREA_CONFIG = {
  rows: 4,
  allowClear: true,
  autoSize: { minRows: 4, maxRows: 4 },
};

const TOOLTIP_MESSAGE = (
  <span>
    Para poder habilitar el botón de acreditar necesita seleccionar
    cargos de un padrón y tener marcado el icono
    {' '}
    <PushpinFilled />
    {' '}
    del padron a cual desea acreditar.
  </span>
);

const ModalAcreditacion = ({ disabled, pinPadron }) => {
  const dispatch = useDispatch();
  const { corte } = useSelector(({ app }) => app);
  const { selectedRowKeys, ciudadano } = useSelector(({ caja }) => caja);

  const [form] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conceptos, setConceptos] = useState([]);

  const [base64, setBase64] = useState('');
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [error, setError] = useState('');

  const cargos = useMemo(() => {
    if (!pinPadron?.cargos?.length) return [];
    if (!selectedRowKeys[pinPadron?.key]?.length) return [];

    const selectedCargos = pinPadron.cargos
      .filter((c) => selectedRowKeys[pinPadron.key]?.includes(c.id));
    const recalculatedCargos = recalculateCargos(
      selectedCargos,
      selectedRowKeys[pinPadron.key],
    );
    return sortCargos(recalculatedCargos);
  }, [pinPadron?.cargos, pinPadron?.key, selectedRowKeys]);

  const totalImporte = useMemo(
    () => cargos.reduce((acc, curr) => acc + curr.adeudo_total, 0),
    [cargos],
  );

  useEffect(() => {
    let mounted = true;

    const fetchConceptos = async () => {
      if (disabled || !isModalOpen) return;

      try {
        setLoading(true);
        const conceptosData = await getConceptosDeAcreditacion();
        if (mounted) {
          setConceptos(conceptosData);
        }
      } catch (fetchError) {
        if (mounted) {
          onError(fetchError);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchConceptos();
    return () => { mounted = false; };
  }, [isModalOpen, disabled]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleFinish = useCallback(async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const payload = {
        padrones: [
          {
            cargos: cargos.map((c) => c.id),
            padron_id: pinPadron.id,
            tipo_de_padron: pinPadron.tipo_de_padron,
            observaciones: pinPadron.observaciones,
            importe: totalImporte,
          },
        ],
        ciudadano: ciudadano.id,
        ...values,
      };

      const response = await postReciboAcreditador(payload);
      if (response?.recibos?.length) {
        const pdfResponse = await getRecibosPDF(response.recibos.map((r) => r.id));
        const { success, base64: recibosPDFBase64 } = pdfResponse;

        if (success) {
          setBase64(recibosPDFBase64);
          setVisiblePreview(true);
          handleCancel();
        } else {
          const errorMsg = 'No se pudo obtener el recibo';
          setError(errorMsg);
          message.error(errorMsg);
        }
      }
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [cargos, pinPadron, totalImporte, ciudadano?.id, form, handleCancel]);

  const showModal = useCallback(() => {
    form.resetFields();
    setIsModalOpen(true);
  }, [form]);

  const handlePreviewClose = useCallback((visible) => {
    setVisiblePreview(visible);
    if (!visible) {
      const key = pinPadron.key.split('__')[0];
      dispatch({ type: SET_PADRON_REQUIERE_FETCH, payload: key });
      setBase64('');
      setError('');
    }
  }, [pinPadron?.key, dispatch]);

  const renderButton = useMemo(() => {
    if (!corte) {
      return (
        <Tooltip title="No hay un corte abierto">
          <Button size="large" style={BUTTON_STYLES} disabled>Acreditar</Button>
        </Tooltip>
      );
    }
    if (disabled) {
      return (
        <Tooltip title={TOOLTIP_MESSAGE}>
          <Button
            size="large"
            style={BUTTON_STYLES}
            disabled
          >
            Acreditar
          </Button>
        </Tooltip>
      );
    }

    return (
      <Col>
        <Button
          onClick={showModal}
          size="large"
          style={SUCCESS_BUTTON_STYLES}
        >
          Acreditar
        </Button>
      </Col>
    );
  }, [disabled, showModal]);

  return (
    <>
      {renderButton}
      <Modal
        open={isModalOpen}
        footer={null}
        closable={false}
        centered
        title={(
          <FormSubmitControls
            onFinish={handleFinish}
            onCancel={handleCancel}
            loading={loading}
            label="Acreditar"
            onFinishText="Acreditar"
            onFinishIcon={<CheckOutlined />}
          />
        )}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  name="concepto_de_acreditacion_id"
                  label="Concepto de acreditación"
                  rules={[{ required: true, message: 'El concepto de acreditación es requerido' }]}
                >
                  <Select
                    dataSource={conceptos}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Comentarios de la acreditación"
                  name="observaciones_de_acreditacion"
                  rules={[{ required: true, message: 'Los comentarios de la acreditación son requeridos' }]}
                >
                  <Input.TextArea
                    rows={TEXTAREA_CONFIG.rows}
                    allowClear={TEXTAREA_CONFIG.allowClear}
                    autoSize={TEXTAREA_CONFIG.autoSize}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
      <Previewer
        allowDownload
        allowSend
        base64={base64}
        visible={visiblePreview}
        setVisible={handlePreviewClose}
        error={error}
      />
    </>
  );
};

ModalAcreditacion.propTypes = {
  disabled: PropTypes.bool.isRequired,
  pinPadron: PropTypes.shape({
    key: PropTypes.string.isRequired,
    cargos: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      adeudo_total: PropTypes.number.isRequired,
    })).isRequired,
    id: PropTypes.number.isRequired,
    tipo_de_padron: PropTypes.number.isRequired,
    observaciones: PropTypes.string,
  }).isRequired,
};

export default ModalAcreditacion;
